import { useCallback, useEffect, useRef, useState } from 'react';
import { needsContact } from '../../shared/claim';
import type { Segment } from '../../shared/types';
import { Confetti } from '../components/Confetti';
import { PrizeModal } from '../components/PrizeModal';
import { Wheel3D, type WheelHandle } from '../components/Wheel3D';
import { api } from '../lib/api';
import './Spinner.css';

/**
 * `?preview=prize` abre el modal del premio gordo sin girar y sin tocar el
 * servidor, para poder revisar el diseno del formulario sin depender de la
 * suerte. Solo funciona en la build de desarrollo (`npm run dev`, :5173):
 * en produccion `import.meta.env.DEV` se compila a `false` y esta funcion
 * queda en un `return false`, asi que el parametro no hace nada. Se cierra
 * aposta: un visitante con la url podria ensenar en el stand un premio que
 * no ha ganado.
 */
function wantsPreview(): boolean {
  return (
    import.meta.env.DEV && new URLSearchParams(window.location.search).get('preview') === 'prize'
  );
}

export function Spinner() {
  const [segments, setSegments] = useState<Segment[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // El premio que se ensena y, si este premio los pide, el registro que hay
  // que completar con los datos del ganador.
  const [result, setResult] = useState<{ extra: string; claimId: string | null } | null>(null);
  const [confetti, setConfetti] = useState(0);
  const [jackpot, setJackpot] = useState(false);
  const wheelRef = useRef<WheelHandle>(null);

  const preview = wantsPreview();

  useEffect(() => {
    api
      .wheel()
      .then((data) => {
        setSegments(data.segments);
        // El texto del premio sale de la configuracion real, no inventado:
        // asi la vista previa ensena lo que veria un ganador de verdad.
        if (preview) {
          const gordo = data.segments.find((seg) => needsContact(seg.prizeId));
          setResult({ extra: gordo?.extra ?? '', claimId: 'preview' });
          setJackpot(true);
          setConfetti((n) => n + 1);
        }
      })
      .catch(() => setLoadError('Could not load the wheel. Check the connection and reload.'));
  }, [preview]);

  const spin = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    try {
      // El servidor decide primero; la ruleta solo representa esa decision.
      const outcome = await api.spin();
      await wheelRef.current?.spinTo(outcome.segmentIndex);
      setResult({ extra: outcome.extra, claimId: outcome.claimId });
      // Siempre se gana gasolina, asi que siempre hay confeti; el premio
      // gordo solo sube la intensidad.
      setJackpot(Boolean(outcome.prizeId));
      setConfetti((n) => n + 1);
    } catch {
      setLoadError('Something went wrong. Try again.');
    } finally {
      setBusy(false);
    }
  }, [busy]);

  return (
    <main className="spinner-page">
      {segments.length > 0 && (
        <Wheel3D ref={wheelRef} segments={segments} disabled={busy} onSpin={spin} />
      )}

      {/* La pantalla es solo ruleta: el estado se anuncia a lectores de
          pantalla, y solo el error se hace visible (si no, un fallo de carga
          dejaria la pagina en blanco sin explicacion). */}
      <p className="spinner-status" role="status">
        {busy ? 'Good luck…' : ''}
      </p>

      {loadError !== null && <p className="spinner-error">{loadError}</p>}

      <Confetti fire={confetti} intensity={jackpot ? 'jackpot' : 'normal'} />
      {result !== null && (
        <PrizeModal
          extra={result.extra}
          claimId={result.claimId}
          // En la vista previa no hay premio detras: enviar solo ensena el
          // estado de guardado, no escribe nada en el log.
          onClaim={preview ? async () => undefined : api.claim}
          onClose={() => setResult(null)}
        />
      )}
    </main>
  );
}
